import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getDefaultStore } from 'jotai';
import { currentLangAtom } from '@/store/langAtom';
import {
  detectBrowserLanguage,
  readUserSelectedLanguage,
  writeUserSelectedLanguage,
} from '@/lib/langPreference';
// en.json is the only i18next bundle — short UI-chrome strings (nav labels, button text,
// ARIA strings) that must be synchronous and survive a Content API outage.
// All user-facing verbiage goes through content-defaults.json + paragraph config API.
import en from '@/locales/en.json';

export { detectBrowserLanguage };

export const LANGUAGES = [
  { code: 'en', label: 'English',  dir: 'ltr' },
  { code: 'es', label: 'Español',  dir: 'ltr' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
] as const;

export type LanguageCode = typeof LANGUAGES[number]['code'];

/* ── Browser language detection is in langPreference (re-exported above) ── */

function isKnownBundleCode(code: string | null): code is LanguageCode {
  return LANGUAGES.some(l => l.code === code);
}

/* ── Resolve initial language (saved choice, otherwise the browser language) ── */
function resolveInitialLanguage(): string {
  return readUserSelectedLanguage() ?? detectBrowserLanguage();
}

/* ── Resolve i18next bundle code from a full ISO code ── */
// Full ISO code (e.g. 'en-AU') → bundle prefix ('en') when no exact bundle exists.
function resolveBundleCode(code: string): LanguageCode {
  if (isKnownBundleCode(code)) return code;
  const prefix = code.split('-')[0]!.toLowerCase();
  if (isKnownBundleCode(prefix)) return prefix as LanguageCode;
  return 'en';
}

/* ── Apply language (call this on every language switch) ── */
// Accepts any app_language_code string from l_app_language — 'en', 'en-AU', 'hr', etc.
// Writing to currentLangAtom is the primary reactive update; all useAtomValue(currentLangAtom)
// subscribers re-render automatically without needing an i18next event listener.
// persist writes localStorage + pp_lang. Automatic defaults pass persist: false
// so a browser or facility language is not stored as the visitor's choice.
export function applyLanguage(code: string, options?: { persist?: boolean }): void {
  if (options?.persist !== false) writeUserSelectedLanguage(code);

  getDefaultStore().set(currentLangAtom, code);

  void i18n.changeLanguage(resolveBundleCode(code));

  document.documentElement.lang = code;
  document.documentElement.dir = code.startsWith('ar') ? 'rtl' : 'ltr';
}

/* ── Initialise ── */
const initialCode = resolveInitialLanguage();
const initialBundleCode = resolveBundleCode(initialCode);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: initialBundleCode,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Sync <html lang> and <html dir> on startup
document.documentElement.lang = initialCode;
document.documentElement.dir = initialCode.startsWith('ar') ? 'rtl' : 'ltr';

export default i18n;
